/**
 * Project detail/overview page.
 *
 * Shows project settings summary, navigation to members/tags.
 * Edit and archive buttons for project admins.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@frontend/components/ui/alert-dialog"
import { Badge } from "@frontend/components/ui/badge"
import { Button } from "@frontend/components/ui/button"
import { Card, CardContent } from "@frontend/components/ui/card"
import { DataTable } from "@frontend/components/ui/data-table"
import { Input } from "@frontend/components/ui/input"
import { useApiClient, useApiQuery } from "@frontend/hooks/use-api"
import { useArchiveProject, useDeleteProject, useProject, useUnarchiveProject } from "@frontend/hooks/use-projects"
import { useToggleSet } from "@frontend/hooks/use-toggle-set"
import { CaseLinkDialog } from "@frontend/pages/project-detail/components/case-link-dialog"
import { CaseUploadDialog } from "@frontend/pages/project-detail/components/case-upload-dialog"
import { formatTimeAgo } from "@frontend/pages/project-detail/lib/time"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { ColumnDef } from "@tanstack/react-table"
import {
  Archive,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Eye,
  Link2,
  Play,
  RotateCcw,
  Search,
  Settings,
  Trash2,
  Upload,
  Users
} from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router"

type CaseStatus =
  | "not_started"
  | "in_progress"
  | "submitted"
  | "under_review"
  | "revision_needed"
  | "accepted"
  | "accepted_with_edits"

interface CaseSubmission {
  id: string
  user_id: string
  user_name: string
  status: string
  submitted_at: string | null
  reviewed_by: string | null
  updated_at: string
  review_comment: string | null
}

interface CaseItem {
  id: string
  study_identifier: string
  study_description: string | null
  sort_metric_value: number | null
  overall_case_status: CaseStatus
  my_submission_status: Exclude<CaseStatus, "not_started"> | null
  my_submission_id: string | null
  submission_count: number
  claim_slots: number
  can_open_for_annotation: boolean
  submitted_at: string | null
  reviewed_at: string | null
  updated_at: string
  submissions?: CaseSubmission[]
}

interface ProjectMemberItem {
  user_id: string
  role: "project_admin" | "annotator" | "reviewer" | "viewer"
}

const CASE_STATUS_LABEL: Record<CaseStatus, string> = {
  not_started: "Not Started",
  in_progress: "In Progress",
  submitted: "Submitted",
  under_review: "Under Review",
  revision_needed: "Revision Needed",
  accepted: "Accepted",
  accepted_with_edits: "Accepted (Edits)"
}

const CASE_STATUS_STYLE: Record<
  CaseStatus,
  { variant: "secondary" | "default" | "outline" | "destructive"; className?: string }
> = {
  not_started: { variant: "outline" },
  in_progress: { variant: "secondary", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  submitted: { variant: "default" },
  under_review: {
    variant: "secondary",
    className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
  },
  revision_needed: { variant: "destructive" },
  accepted: {
    variant: "secondary",
    className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
  },
  accepted_with_edits: {
    variant: "secondary",
    className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
  }
}

const SUBMISSION_STATUS_LABEL: Record<string, string> = {
  in_progress: "In Progress",
  submitted: "Submitted",
  under_review: "Under Review",
  revision_needed: "Revision Needed",
  accepted: "Accepted",
  accepted_with_edits: "Accepted (Edits)"
}

type StatusFilter = "all" | "submitted" | "under_review" | "revision_needed" | "accepted"

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "submitted", label: "Submitted" },
  { value: "under_review", label: "Under Review" },
  { value: "revision_needed", label: "Needs Revision" },
  { value: "accepted", label: "Accepted" }
]

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const api = useApiClient()
  const storageKey = `project-cases-${projectId}`
  const readStorage = () => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) ?? "{}")
    } catch {
      return {}
    }
  }
  const persist = useCallback(
    (patch: Record<string, unknown>) => {
      try {
        const current = JSON.parse(localStorage.getItem(storageKey) ?? "{}")
        localStorage.setItem(storageKey, JSON.stringify({ ...current, ...patch }))
      } catch {
        /* ignore */
      }
    },
    [storageKey]
  )
  const [tablePageIndex, setTablePageIndex] = useState<number>(() => readStorage().tablePageIndex ?? 0)
  const [search, setSearchRaw] = useState<string>(() => readStorage().q ?? "")
  const [debouncedSearch, setDebouncedSearch] = useState<string>(search)
  const [statusFilter, setStatusFilterRaw] = useState<StatusFilter>(() => readStorage().status ?? "all")

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const setSearch = (value: string) => {
    setSearchRaw(value)
    setTablePageIndex(0)
    persist({ q: value, tablePageIndex: 0 })
  }
  const setStatusFilter = (value: StatusFilter) => {
    setStatusFilterRaw(value)
    setTablePageIndex(0)
    persist({ status: value, tablePageIndex: 0 })
  }

  const [expandedCaseIds, toggleExpanded] = useToggleSet<string>()
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [casePendingDelete, setCasePendingDelete] = useState<CaseItem | null>(null)
  const [submissionPendingReopen, setSubmissionPendingReopen] = useState<{
    caseItem: CaseItem
    submission: CaseSubmission
  } | null>(null)
  const { data: project, isLoading } = useProject(projectId!)
  const { data: currentUser } = useApiQuery<{
    id: string
    instance_role: "admin" | "member" | null
  }>(["auth", "me"], "/auth/me")
  const casesPage = tablePageIndex + 1
  const casesLimit = 20
  const { data: projectMembersResult } = useApiQuery<{ data: ProjectMemberItem[] }>(
    ["projects", projectId!, "members", "for-case-management"],
    `/projects/${projectId}/members?limit=100&page=1`,
    { enabled: Boolean(projectId) && Boolean(currentUser?.id) }
  )
  const archiveProject = useArchiveProject(projectId!)
  const unarchiveProject = useUnarchiveProject(projectId!)
  const deleteProjectMutation = useDeleteProject(projectId!)

  const isAdmin = currentUser?.instance_role === "admin"
  const isProjectAdmin =
    Boolean(currentUser?.id) &&
    (projectMembersResult?.data.some(
      (member) => member.user_id === currentUser?.id && member.role === "project_admin"
    ) ??
      false)
  const canManageCases =
    isAdmin ||
    project?.current_user_role === "instance_admin" ||
    project?.current_user_role === "project_admin" ||
    isProjectAdmin

  const { data: sharedSourcesResult } = useApiQuery<{
    data: { name: string; path: string }[]
  }>(["shared-sources"], "/shared-sources", { enabled: canManageCases })
  const hasSharedSources = (sharedSourcesResult?.data?.length ?? 0) > 0

  const isReviewer = canManageCases || project?.current_user_role === "reviewer"

  const isQueuedAnnotator = project?.mode === "queued" && !canManageCases && !isReviewer

  const { data: nextCaseResult, isLoading: nextCaseLoading } = useApiQuery<{
    data: CaseItem | null
    message?: string
  }>(["projects", projectId!, "cases", "next"], `/projects/${projectId}/cases/next`, {
    enabled: Boolean(isQueuedAnnotator)
  })

  const hasQueuedSort = project?.mode === "queued" && Boolean(project?.sort_metric_key)
  const casesQueryParams = new URLSearchParams({
    page: String(casesPage),
    limit: String(casesLimit)
  })
  if (hasQueuedSort) {
    casesQueryParams.set("sort", "sort_metric_value")
    casesQueryParams.set("order", project?.sort_metric_direction ?? "asc")
  }
  if (isQueuedAnnotator) {
    casesQueryParams.set("mine", "true")
  }
  if (debouncedSearch) casesQueryParams.set("search", debouncedSearch)
  if (statusFilter !== "all") casesQueryParams.set("status", statusFilter)

  const casesSortKey = hasQueuedSort ? "sort_metric_value" : "created_at"
  const { data: casesResult, isLoading: casesLoading } = useApiQuery<{
    data: CaseItem[]
    total: number
    page: number
    limit: number
  }>(
    [
      "projects",
      projectId!,
      "cases",
      `page=${casesPage}`,
      `search=${debouncedSearch}`,
      `status=${statusFilter}`,
      `sort=${casesSortKey}`,
      `mine=${Boolean(isQueuedAnnotator)}`
    ],
    `/projects/${projectId}/cases?${casesQueryParams}`,
    { placeholderData: (prev) => prev }
  )

  const deleteCaseMutation = useMutation({
    mutationFn: (caseId: string) =>
      api(`/cases/${caseId}`, {
        method: "DELETE"
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId!, "cases"] })
      setCasePendingDelete(null)
    }
  })

  const reopenSubmissionMutation = useMutation({
    mutationFn: (submissionId: string) =>
      api(`/submissions/${submissionId}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "in_progress" })
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId!, "cases"] })
      setSubmissionPendingReopen(null)
    }
  })

  const handleArchive = () => {
    if (confirm("Are you sure you want to archive this project? It can be unarchived later.")) {
      archiveProject.mutate(undefined, {
        onSuccess: () => {
          navigate(`/`)
        }
      })
    }
  }

  const handleUnarchive = () => {
    unarchiveProject.mutate()
  }

  const handleDelete = () => {
    if (
      confirm(
        `Are you sure you want to permanently delete "${project?.name}"? This action cannot be undone. All cases, annotations, and associated files will be removed.`
      )
    ) {
      deleteProjectMutation.mutate(undefined, {
        onSuccess: () => {
          navigate(`/`)
        }
      })
    }
  }

  const columns = useMemo<ColumnDef<CaseItem>[]>(
    () => [
      ...(isReviewer
        ? [
            {
              id: "expand",
              accessorFn: () => null,
              header: "",
              size: 32,
              cell: ({ row }: { row: { original: CaseItem } }) => {
                const hasSubmissions = (row.original.submissions?.length ?? 0) > 0
                if (!hasSubmissions) return null
                const isExpanded = expandedCaseIds.has(row.original.id)
                return (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    aria-label={isExpanded ? "Collapse submissions" : "Expand submissions"}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleExpanded(row.original.id)
                    }}
                  >
                    {isExpanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                  </Button>
                )
              }
            } as ColumnDef<CaseItem>
          ]
        : []),
      {
        accessorKey: "study_identifier",
        header: "Case",
        cell: ({ row }) => <span className="font-medium">{row.original.study_identifier}</span>
      },
      {
        id: "studyUid",
        accessorFn: (row) => row.study_identifier,
        header: "Study UID",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-[11px] font-mono">
            {row.original.study_identifier.slice(0, 22)}...
          </span>
        )
      },
      {
        id: "status",
        accessorFn: (row) => row.overall_case_status,
        header: "Status",
        cell: ({ row }) => {
          const status = row.original.overall_case_status
          const style = CASE_STATUS_STYLE[status]
          return (
            <Badge variant={style.variant} className={`text-[10px] ${style.className ?? ""}`}>
              {CASE_STATUS_LABEL[status]}
            </Badge>
          )
        }
      },
      {
        id: "submissions",
        accessorFn: (row) => row.submission_count,
        header: "Readers",
        cell: ({ row }) => {
          const names = isReviewer ? (row.original.submissions?.map((s) => s.user_name) ?? []) : []
          return (
            <div>
              <span className="text-[11px] font-mono tabular-nums">
                {row.original.submission_count}/{row.original.claim_slots}
              </span>
              {names.length > 0 && (
                <div className="text-[11px] text-muted-foreground truncate max-w-[160px]" title={names.join(", ")}>
                  {names.join(", ")}
                </div>
              )}
            </div>
          )
        }
      },
      ...(project?.sort_metric_key
        ? [
            {
              accessorKey: "sort_metric_value",
              header: project.sort_metric_key,
              cell: ({ row }: { row: { original: CaseItem } }) => (
                <span className="text-[11px] font-mono tabular-nums">{row.original.sort_metric_value ?? "—"}</span>
              )
            } as ColumnDef<CaseItem>
          ]
        : []),
      {
        accessorKey: "updated_at",
        header: "Updated",
        cell: ({ row }) => (
          <span className="text-[12px] font-mono tabular-nums text-muted-foreground">
            {new Date(row.original.updated_at).toLocaleDateString()}
          </span>
        )
      },
      {
        id: "actions",
        accessorFn: (row) => row.id,
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label="Open case"
              onClick={(e) => {
                e.stopPropagation()
                if (!row.original.can_open_for_annotation) return
                navigate(`/projects/${projectId}/cases/${row.original.id}`)
              }}
              disabled={!row.original.can_open_for_annotation}
            >
              <Eye className="size-3.5" />
            </Button>
            {canManageCases ? (
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-destructive hover:text-destructive"
                aria-label="Delete case"
                onClick={(e) => {
                  e.stopPropagation()
                  setCasePendingDelete(row.original)
                }}
              >
                <Trash2 className="size-3.5" />
              </Button>
            ) : null}
          </div>
        )
      }
    ],
    [canManageCases, expandedCaseIds, isReviewer, navigate, projectId, project?.sort_metric_key, toggleExpanded]
  )

  const cases = casesResult?.data ?? []
  const totalCases = casesResult?.total ?? 0
  const pageCount = Math.ceil(totalCases / casesLimit)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-muted-foreground">Loading project...</p>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-muted-foreground">Project not found.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/`}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1>{project.name}</h1>
            <p className="text-muted-foreground text-[12px] mt-1">
              {totalCases} cases • {project.mode} mode
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/projects/${projectId}/edit`}>
                  <Settings className="h-4 w-4 mr-1" />
                  Edit
                </Link>
              </Button>
              {project.status === "active" && (
                <Button variant="outline" size="sm" onClick={handleArchive} disabled={archiveProject.isPending}>
                  <Archive className="h-4 w-4 mr-1" />
                  Archive
                </Button>
              )}
              <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleteProjectMutation.isPending}>
                <Trash2 className="h-4 w-4 mr-1" />
                {deleteProjectMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </>
          )}
          {canManageCases && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/projects/${projectId}/edit?tab=members`}>
                <Users className="h-4 w-4 mr-1" />
                Members
              </Link>
            </Button>
          )}
          {canManageCases && hasSharedSources ? (
            <Button variant="outline" size="sm" onClick={() => setLinkDialogOpen(true)}>
              <Link2 className="h-4 w-4 mr-1" />
              Link from Source
            </Button>
          ) : null}
          {canManageCases ? (
            <Button size="sm" onClick={() => setUploadDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-1" />
              Upload Cases
            </Button>
          ) : null}
        </div>
      </div>

      {project.status === "archived" && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-sm flex items-center justify-between">
          <span>This project is archived.</span>
          {canManageCases && (
            <Button
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-800 hover:bg-amber-100"
              onClick={handleUnarchive}
              disabled={unarchiveProject.isPending}
            >
              {unarchiveProject.isPending ? "Restoring..." : "Unarchive"}
            </Button>
          )}
        </div>
      )}

      {/* Status filter tabs for reviewers */}
      {isReviewer && (
        <div className="flex items-center gap-1 border-b border-border pb-0">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={`px-3 py-1.5 text-[11px] font-medium border-b-2 transition-colors -mb-px ${
                statusFilter === filter.value
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            placeholder="Search cases..."
            className="pl-8 h-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="ml-auto text-[11px] text-muted-foreground font-mono tabular-nums">{totalCases} cases</span>
      </div>

      {isQueuedAnnotator && (
        <Card>
          <CardContent className="p-5">
            {nextCaseLoading ? (
              <p className="text-sm text-muted-foreground">Finding your next case...</p>
            ) : nextCaseResult?.data ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Next Case</p>
                  <p className="font-medium">{nextCaseResult.data.study_identifier}</p>
                  {nextCaseResult.data.sort_metric_value != null && project?.sort_metric_key && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {project.sort_metric_key}:{" "}
                      <span className="font-mono">{nextCaseResult.data.sort_metric_value}</span>
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => {
                    const caseId = nextCaseResult?.data?.id
                    if (caseId) navigate(`/projects/${projectId}/cases/${caseId}`)
                  }}
                >
                  <Play className="size-4 mr-1.5" />
                  Open Case
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No cases available — you've completed all assigned cases.</p>
            )}
          </CardContent>
        </Card>
      )}

      {isQueuedAnnotator && cases.length > 0 && (
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">My Cases</p>
      )}

      <div className="bg-card rounded-lg border overflow-auto">
        {casesLoading ? (
          <Card>
            <CardContent className="p-5 text-sm text-muted-foreground">Loading cases...</CardContent>
          </Card>
        ) : cases.length === 0 ? (
          <Card>
            <CardContent className="p-5 text-sm text-muted-foreground">No cases yet for this project.</CardContent>
          </Card>
        ) : (
          <DataTable
            columns={columns}
            data={cases}
            emptyMessage="No cases match your search."
            showPagination
            manualPagination
            pageCount={pageCount}
            pageSize={casesLimit}
            initialPageIndex={tablePageIndex}
            onPageChange={(idx) => {
              setTablePageIndex(idx)
              persist({ tablePageIndex: idx })
            }}
            onRowClick={(c) => {
              const hasEditableSubmission =
                c.my_submission_status === "in_progress" || c.my_submission_status === "revision_needed"
              if (hasEditableSubmission) {
                navigate(`/projects/${projectId}/cases/${c.id}`)
                return
              }
              if (isReviewer && (c.submissions?.length ?? 0) > 0) {
                toggleExpanded(c.id)
                return
              }
              if (!c.can_open_for_annotation) return
              navigate(`/projects/${projectId}/cases/${c.id}`)
            }}
            renderSubRow={(caseItem) => {
              if (!(isReviewer && expandedCaseIds.has(caseItem.id))) return null
              const subs = caseItem.submissions ?? []
              if (subs.length === 0) return null
              const hasSortMetric = Boolean(project?.sort_metric_key)
              return (
                <>
                  {subs.map((sub) => (
                    <tr key={sub.id} className="bg-muted/30 border-t border-border">
                      {/* expand column spacer */}
                      <td />
                      {/* Case → empty */}
                      <td />
                      {/* Study UID → empty */}
                      <td />
                      {/* Status */}
                      <td className="p-2">
                        <Badge
                          variant={(CASE_STATUS_STYLE[sub.status as CaseStatus] ?? { variant: "outline" }).variant}
                          className={`text-[10px] ${CASE_STATUS_STYLE[sub.status as CaseStatus]?.className ?? ""}`}
                        >
                          {SUBMISSION_STATUS_LABEL[sub.status] ?? sub.status}
                        </Badge>
                      </td>
                      {/* Readers → annotator name */}
                      <td className="p-2">
                        <span className="text-[12px] font-medium text-foreground truncate" title={sub.user_name}>
                          {sub.user_name}
                        </span>
                      </td>
                      {/* Sort metric → empty (only if column exists) */}
                      {hasSortMetric && <td />}
                      {/* Updated */}
                      <td className="p-2">
                        <span
                          className="text-[12px] font-mono tabular-nums text-muted-foreground"
                          title={new Date(sub.updated_at).toLocaleString()}
                        >
                          {formatTimeAgo(sub.updated_at)}
                        </span>
                      </td>
                      {/* Actions */}
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          {["submitted", "under_review"].includes(sub.status) ? (
                            <Button
                              size="sm"
                              className="h-6 px-2 text-[10px]"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/projects/${projectId}/cases/${caseItem.id}/review/${sub.id}`)
                              }}
                            >
                              <ClipboardCheck className="size-3 mr-1" />
                              Review
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[10px]"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/projects/${projectId}/cases/${caseItem.id}/review/${sub.id}`)
                              }}
                            >
                              <Eye className="size-3 mr-1" />
                              View
                            </Button>
                          )}
                          {canManageCases &&
                          (sub.status === "submitted" || (sub.status === "accepted" && sub.reviewed_by === null)) ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[10px]"
                              onClick={(e) => {
                                e.stopPropagation()
                                setSubmissionPendingReopen({ caseItem, submission: sub })
                              }}
                            >
                              <RotateCcw className="size-3 mr-1" />
                              Reopen
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              )
            }}
          />
        )}
      </div>

      <CaseUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        projectId={projectId!}
        workflow={project.workflow === "pathology" ? "pathology" : "radiology"}
      />

      {hasSharedSources && (
        <CaseLinkDialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen} projectId={projectId!} />
      )}

      <AlertDialog
        open={Boolean(submissionPendingReopen)}
        onOpenChange={(open) => {
          if (!open) {
            setSubmissionPendingReopen(null)
            reopenSubmissionMutation.reset()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen Submission</AlertDialogTitle>
            <AlertDialogDescription>
              Reopen the submission from {submissionPendingReopen?.submission.user_name} for case{" "}
              <span className="font-mono">{submissionPendingReopen?.caseItem.study_identifier}</span>? The annotator
              will be able to continue editing and submit it again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {reopenSubmissionMutation.isError ? (
            <p className="text-xs text-destructive">{reopenSubmissionMutation.error.message}</p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reopenSubmissionMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={reopenSubmissionMutation.isPending || !submissionPendingReopen}
              onClick={() => {
                if (!submissionPendingReopen) return
                reopenSubmissionMutation.mutate(submissionPendingReopen.submission.id)
              }}
            >
              {reopenSubmissionMutation.isPending ? "Reopening..." : "Reopen Submission"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(casePendingDelete)}
        onOpenChange={(open) => {
          if (!open) {
            setCasePendingDelete(null)
            deleteCaseMutation.reset()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Case</AlertDialogTitle>
            <AlertDialogDescription>
              Remove case <span className="font-mono">{casePendingDelete?.study_identifier}</span> from this project?
              This also deletes associated local files.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteCaseMutation.isError ? (
            <p className="text-xs text-destructive">{deleteCaseMutation.error.message}</p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteCaseMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteCaseMutation.isPending || !casePendingDelete}
              onClick={() => {
                if (!casePendingDelete) return
                deleteCaseMutation.mutate(casePendingDelete.id)
              }}
            >
              {deleteCaseMutation.isPending ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
